import type { AdminShareLink } from '../../../shared/types';
import { formatShortDate, quietSmButtonClass } from './AdminUi';
import CopyButton from './CopyButton';

export type ShareLinksDisclosureAction = 'toggle' | 'link-created';

export function nextShareLinksExpanded(
  current: boolean,
  action: ShareLinksDisclosureAction,
): boolean {
  return action === 'link-created' ? true : !current;
}

export default function ShareLinksDisclosure({
  links,
  expanded,
  busyAction,
  onToggle,
  onRevoke,
  onReissue,
}: {
  links: AdminShareLink[];
  expanded: boolean;
  busyAction: 'create' | string | null;
  onToggle: () => void;
  onRevoke: (linkId: string) => void;
  onReissue: (linkId: string) => void;
}) {
  if (links.length === 0) {
    return <p className="text-sm text-muted">No share links yet.</p>;
  }

  const activeCount = links.filter((link) => link.revoked_at === null).length;

  return (
    <>
      <button
        id="existing-share-links-toggle"
        type="button"
        className="flex w-full items-center gap-2 text-left text-sm font-semibold text-fg"
        aria-expanded={expanded}
        aria-controls="existing-share-links-list"
        onClick={onToggle}
      >
        <svg className="h-3 w-3 shrink-0 text-faint" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          {expanded ? (
            <path d="m3 4.5 3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          ) : (
            <path d="m4.5 3 3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          )}
        </svg>
        <span>Existing links</span>
        <span className="font-mono text-[10px] font-normal uppercase tracking-[0.1em] text-faint">
          {links.length} {links.length === 1 ? 'link' : 'links'} · {activeCount} active
        </span>
      </button>

      {expanded ? (
        <div className="mt-3" role="region" aria-labelledby="existing-share-links-toggle">
          <ul id="existing-share-links-list" className="divide-y divide-line border-y border-line">
            {links.map((link) => {
              const active = link.revoked_at === null;
              const shareUrl = link.share_url;
              return (
                <li
                  key={link.id}
                  className={`flex min-h-10 flex-wrap items-center gap-x-3 gap-y-1 py-1.5${active ? '' : ' opacity-55'}`}
                >
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${active ? 'bg-accent' : 'bg-faint'}`}
                    aria-hidden="true"
                  />
                  <span className="text-sm font-semibold">{link.mode === 'pin' ? 'PIN protected' : 'Open'}</span>
                  <span className="font-mono text-[10px] text-faint">
                    created {formatShortDate(link.created_at)}
                    {link.revoked_at !== null ? ` · revoked ${formatShortDate(link.revoked_at)}` : ''}
                  </span>
                  {active ? (
                    <div className="ml-auto flex flex-wrap items-center justify-end gap-1">
                      {shareUrl ? (
                        <CopyButton text={shareUrl} successMessage="Link copied." />
                      ) : (
                        <div className="text-right">
                          <button type="button" className={quietSmButtonClass}
                            onClick={() => onReissue(link.id)} disabled={busyAction !== null}>
                            {busyAction === `reissue:${link.id}` ? 'Reissuing' : 'Reissue link'}
                          </button>
                          <p className="text-xs text-muted">Replaces the previous link. Open reviews keep working.</p>
                        </div>
                      )}
                      <button
                        type="button"
                        className={quietSmButtonClass}
                        onClick={() => onRevoke(link.id)}
                        disabled={busyAction !== null}
                      >
                        {busyAction === link.id ? 'Revoking' : 'Revoke'}
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </>
  );
}
